import { useSubscription } from '@apollo/client/react';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { Kitchen } from '../libs';
import { SUBSCRIBE_TO_HOURLY_DATA_UPDATE_SUBSCRIPTION } from '../libs/graphQL';

export interface HourlyDataUpdateEvent {
  kitchen: Kitchen;
  date: string;
  updatedAt: string;
}

interface SubscriptionData {
  subscribeToHourlyDataUpdate: HourlyDataUpdateEvent;
}

interface UseHourlyDataSubscriptionProps {
  currentKitchen: Kitchen;
  currentDate: string;
  onRefetchFoods?: () => Promise<any> | void;
  onRefetchComponents?: () => Promise<any> | void;
  enabled?: boolean;
  showNotifications?: boolean;
}

const useHourlyDataSubscription = ({
  currentKitchen,
  currentDate,
  onRefetchFoods,
  onRefetchComponents,
  enabled = true,
  showNotifications = true
}: UseHourlyDataSubscriptionProps) => {
  const currentKitchenRef = useRef(currentKitchen);
  const currentDateRef = useRef(currentDate);
  const onRefetchFoodsRef = useRef(onRefetchFoods);
  const onRefetchComponentsRef = useRef(onRefetchComponents);

  useEffect(() => {
    currentKitchenRef.current = currentKitchen;
    currentDateRef.current = currentDate;
    onRefetchFoodsRef.current = onRefetchFoods;
    onRefetchComponentsRef.current = onRefetchComponents;
  }, [currentKitchen, currentDate, onRefetchFoods, onRefetchComponents]);

  useSubscription<SubscriptionData>(SUBSCRIBE_TO_HOURLY_DATA_UPDATE_SUBSCRIPTION, {
    skip: !enabled,
    variables: { kitchen: currentKitchen },
    fetchPolicy: 'no-cache',
    ignoreResults: true,
    onError: (subscriptionError) => {
      console.error('[HourlyDataSubscription] Subscription error:', subscriptionError);

      if (showNotifications) {
        toast.error('Connection error. Real-time updates may be delayed.', {
          position: 'bottom-right',
          autoClose: 5000
        });
      }
    },
    onData: async ({ data: subscriptionData }) => {
      if (!subscriptionData.data?.subscribeToHourlyDataUpdate) {
        return;
      }

      const event = subscriptionData.data.subscribeToHourlyDataUpdate;

      const isRelevant = event.kitchen === currentKitchenRef.current && event.date === currentDateRef.current;
      if (!isRelevant) return;

      console.log('[HourlyDataSubscription] Relevant update received, refetching data:', event);

      try {
        if (showNotifications) {
          toast.info('Hourly data updated. Refreshing...', {
            position: 'bottom-right',
            autoClose: 2000,
            hideProgressBar: false
          });
        }

        const refetchPromises: Promise<any>[] = [];

        if (onRefetchFoodsRef.current) {
          const result = onRefetchFoodsRef.current();
          if (result && typeof result.then === 'function') {
            refetchPromises.push(result);
          }
        }

        if (onRefetchComponentsRef.current) {
          const result = onRefetchComponentsRef.current();
          if (result && typeof result.then === 'function') {
            refetchPromises.push(result);
          }
        }

        if (refetchPromises.length > 0) {
          await Promise.all(refetchPromises);
        }

        if (showNotifications) {
          toast.success('Data refreshed successfully', {
            position: 'bottom-right',
            autoClose: 2000
          });
        }
      } catch (refetchError) {
        console.error('[HourlyDataSubscription] Error refetching data:', refetchError);

        if (showNotifications) {
          toast.error('Failed to refresh data. Please try again.', {
            position: 'bottom-right',
            autoClose: 3000
          });
        }
      }
    }
  });
};

export default useHourlyDataSubscription;
